/**
 * Curve geometry regeneration against the live scene graph.
 *
 * `generateCurveGeometry` in `modeling/` is pure — it takes `CurveData` and
 * optional pre-sampled `taperScale`. Resolving a curve's taper object by
 * name and cascading updates to dependents both require the scene-object
 * map, which is a store concern. That work lives here.
 *
 * Every non-drag mutation that regenerates a curve's tube mesh should route
 * through these helpers so taper resolution and dependent propagation stay
 * uniform — no ad-hoc `generateCurveGeometry(curve)` + `patchSceneObject`
 * at call sites.
 */

import {
  generateCurveGeometry,
  updateCurveGeometryPositions,
} from "../modeling/curveGeometry.ts";
import { evaluateCurve } from "../modeling/curvemath.ts";
import { computeTaperScale } from "../modeling/curveTaper.ts";
import type { CurveData, SceneObject } from "../modeling/types.ts";
import { patchSceneObject, type SceneObjectMap } from "./sceneObjectMap.ts";

/**
 * Regenerate a curve's tube geometry — resolving its taper reference against
 * `sceneObjects` — and cascade-regenerate any curves that reference it.
 *
 * `newCurveData`, when provided, replaces the object's existing `curveData`
 * before regeneration. Callers pass it when they have freshly-mutated curve
 * data to commit; otherwise the current on-object `curveData` is reused
 * (cascade case).
 *
 * Cycles are broken by `visited`: each regenerated id is added before
 * descending into dependents, so an A→B→A loop stops on the second hop.
 */
export function regenerateCurveGeometryInMap(
  sceneObjects: SceneObjectMap,
  objectId: string,
  newCurveData?: CurveData,
  visited: Set<string> = new Set(),
): SceneObjectMap {
  if (visited.has(objectId)) return sceneObjects;
  const obj = sceneObjects.get(objectId);
  const curveData = newCurveData ?? obj?.curveData;
  if (!obj || !curveData) return sceneObjects;
  visited.add(objectId);

  const taperScale = resolveTaperScale(sceneObjects, objectId, curveData);
  const geo = generateCurveGeometry(curveData, { taperScale });

  let next = patchSceneObject(sceneObjects, objectId, {
    curveData,
    vertices: geo.vertices,
    indices: geo.indices,
    loopNormals: geo.loopNormals,
    topology: geo.topology,
    uvs: geo.uvs,
  });

  // Cascade: any curve whose taperObjectName points to us needs to regen
  // against our updated shape.
  for (const dep of findCurveDependents(next, obj.name)) {
    if (visited.has(dep.id)) continue;
    next = regenerateCurveGeometryInMap(next, dep.id, undefined, visited);
  }
  return next;
}

/**
 * Fast-path position-only regeneration during drag — preserves topology
 * identity so renderer memoization doesn't churn. Cascades to dependents
 * via the same fast path.
 *
 * Returns `null` when any curve in the cascade hits a dimension mismatch
 * (e.g. profile resolution changed); callers fall back to the full-regen
 * path, which rebuilds topology from scratch.
 */
export function regenerateCurvePositionsFastInMap(
  sceneObjects: SceneObjectMap,
  objectId: string,
  newCurveData: CurveData,
  visited: Set<string> = new Set(),
): SceneObjectMap | null {
  if (visited.has(objectId)) return sceneObjects;
  const obj = sceneObjects.get(objectId);
  if (!obj) return sceneObjects;
  visited.add(objectId);

  const taperScale = resolveTaperScale(sceneObjects, objectId, newCurveData);
  const newVertices = updateCurveGeometryPositions(newCurveData, obj.vertices, {
    taperScale,
  });
  if (!newVertices) return null;

  let next = patchSceneObject(sceneObjects, objectId, {
    curveData: newCurveData,
    vertices: newVertices,
  });

  for (const dep of findCurveDependents(next, obj.name)) {
    if (visited.has(dep.id)) continue;
    if (!dep.curveData) continue;
    const depNext = regenerateCurvePositionsFastInMap(
      next,
      dep.id,
      dep.curveData,
      visited,
    );
    if (!depNext) return null;
    next = depNext;
  }
  return next;
}

function resolveTaperScale(
  sceneObjects: SceneObjectMap,
  selfId: string,
  curveData: CurveData,
): Float32Array | null {
  const taperName = curveData.taperObjectName;
  if (!taperName) return null;
  const taperObj = findByName(sceneObjects, taperName);
  // Ignore a self-reference defensively — the picker filter blocks it, but
  // renames could produce one, and resolving it would just multiply us by
  // our own profile.
  if (!taperObj || taperObj.id === selfId || !taperObj.curveData) return null;

  const targetSampleCount = evaluateCurve(curveData).positions.length;
  return computeTaperScale(targetSampleCount, taperObj.curveData);
}

function findByName(
  sceneObjects: SceneObjectMap,
  name: string,
): SceneObject | undefined {
  for (const obj of sceneObjects.values()) {
    if (obj.name === name) return obj;
  }
  return undefined;
}

function findCurveDependents(
  sceneObjects: SceneObjectMap,
  sourceName: string,
): SceneObject[] {
  const deps: SceneObject[] = [];
  for (const obj of sceneObjects.values()) {
    if (obj.curveData?.taperObjectName === sourceName) deps.push(obj);
  }
  return deps;
}
